import { useDrag } from 'react-dnd';
import { List } from 'design-system';
import { useRef, useMemo, useState, useEffect } from 'react';

import { AssessmentDimension } from 'src/services/assessmentDimension';

export interface Subject {
  id: string;
  name: string;
  parentId?: string;
  parent?: Subject;
  parentName?: string;
  grandParentName?: string;
  assessmentDimension?: AssessmentDimension;
  _originalIndex?: number;
  code?: string;
  type?: string;
  typeCode?: string;
}

export const ItemTypes = {
  SUBJECT: 'subject',
};

interface DraggableSubjectItemProps {
  subject: Subject;
  isSelected?: boolean;
}

export default function DraggableSubjectItem({ subject, isSelected = false }: DraggableSubjectItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // 先定义getFullSubjectName函数，再使用它
  const getFullSubjectName = () => {
    // 使用code字段作为科目编码
    const code = subject.code || '';
    
    // 如果存在编码，则在名称后添加
    let nameWithCode = subject.name;
    if (code) {
      nameWithCode = `${subject.name} (${code})`;
    }
    
    let fullName = nameWithCode;
    if (subject.parentName) {
      fullName = `${subject.parentName}/${fullName}`;
      if (subject.grandParentName) {
        fullName = `${subject.grandParentName}/${fullName}`;
      }
    }
    return `${fullName}`;
  };
  
  // 创建稳定的拖拽项对象，避免频繁重新创建
  const dragItem = useMemo(() => ({
    id: subject.id,
    name: subject.name,
    parentId: subject.parentId,
    parentName: subject.parentName,
    grandParentName: subject.grandParentName,
    assessmentDimension: subject.assessmentDimension,
    _originalIndex: subject._originalIndex,
    code: subject.code,
    type: subject.type,
    typeCode: subject.typeCode,
    _dragTime: new Date().toISOString(),
    _dragName: subject.name,
    _dragFullName: getFullSubjectName()
  }), [subject.id, subject.name, subject.parentId, subject.parentName, subject.grandParentName, subject.assessmentDimension, subject._originalIndex, subject.code, subject.type, subject.typeCode]);
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.SUBJECT,
    item: dragItem,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [dragItem]);
  
  // 组件卸载时的清理逻辑
  useEffect(() => () => {
      // 确保组件卸载时拖拽状态被清理
      // 不需要手动设置ref.current = null，React会自动处理
    }, []);
  
  // 直接将拖拽功能附加到整个组件
  drag(ref);
  
  const getItemStyle = () => {
    if (isDragging) {
      return {
        cursor: 'move',
        opacity: 0.5,
        padding: '8px 12px',
        background: '#f7f9fc',
        // border: '1px solid #1890ff',
        borderRadius: '4px',
        marginBottom: '8px',
        transition: 'all 0.3s',
      };
    }
    if (isSelected) {
      return {
        cursor: 'move',
        opacity: 1,
        padding: '8px 12px',
        background: '#f7f9fc',
        // border: '1px solid #1890ff',
        borderRadius: '4px',
        marginBottom: '8px',
        transition: 'all 0.3s',
        // boxShadow: '0 2px 8px rgba(24, 144, 255, 0.2)',
      };
    }
    if (isHovered) {
      return {
        cursor: 'move',
        opacity: 1,
        padding: '8px 12px',
        background: '#f7f9fc',
        // border: '1px solid #1890ff',
        borderRadius: '4px',
        marginBottom: '8px',
        transition: 'all 0.3s',
      };
    }
    return {
      cursor: 'move',
      opacity: 1,
      padding: '8px 12px',
      background: '#f7f9fc',
      // border: '1px solid #f0f0f0',
      borderRadius: '4px',
      marginBottom: '8px',
      transition: 'all 0.3s',
    };
  };
  
  return (
    <div 
      ref={ref}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-subject-id={subject.id}
      data-subject-name={subject.name}
      data-original-index={subject._originalIndex}
    >
      <List.Item
        style={getItemStyle()}
      >
        {getFullSubjectName()}
      </List.Item>
    </div>
  );
} 